import React, {useState, useCallback, useEffect, useMemo} from 'react'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody';
import SMCButton from '../../components/Simple/SMCButton';
import SMCModal from '../../components/core/SMCModal';
import {ObjectTypes, SortDirections} from '../../api/consts';
import Api from '../../api/SMPFetchHelper';
import SMCTable from '../../components/core/SMCTable/SMCTable';
import SMCLabel from '../../components/Simple/SMCLabel';
import {ServiceDomainGetListSchema} from '../../api/DBSchema/ServiceDomain';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useSMCErrorHandler} from '../../utils/errorUtils';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import {buildStringSortCaseInsensitive} from '../../utils/sortUtils';
import {useSort} from '../../utils/hooks';

function SetServiceDomainModal({
    isOpen,
    disableSelectById,
    defaultSelected,
    onRequestClose,
    onSuccess,
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const { sortBasis, sortDirection, setSort } = useSort(ServiceDomainGetListSchema.name, SortDirections.ASCENDING);

    const serviceDomainTableColumns = useMemo(() => ([
        { id: 'name',        accessor: ServiceDomainGetListSchema.name,        header: t('SMP.NAME'),        width: 200, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.name) },
        { id: 'description', accessor: ServiceDomainGetListSchema.description, header: t('SMP.DESCRIPTION'), width: 350, sort: buildStringSortCaseInsensitive(ServiceDomainGetListSchema.description) }
    ]), [t]);

    const [selectedObjects, setSelectedObjects] = useState([]);
    const [isTableDataLoading, setIsTableDataLoading] = useState(false);
    const [serviceDomainList, setServiceDomainList] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);

    const [searchString, setSearchString] = useState('');
    const [serviceDomainFilteredList, setServiceDomainFilteredList] = useState([]);

    const loadData = useCallback(() => {
        setIsTableDataLoading(true);
        Api.fetchObjectList(ObjectTypes.SERVICE_DOMAIN)
        .then((resp) => {
            setServiceDomainList(resp.data.data);
        })
        .catch(e => {
            const errorMessage = getErrorMessage(e);
            setErrorMessage(errorMessage);
        })
        .finally(() => {
            setIsTableDataLoading(false);
        });
    }, [getErrorMessage]);

    useEffect(() => {
        if (serviceDomainList && searchString) {
            const filteredList = serviceDomainList.filter(serviceDomain => (
                serviceDomain &&
                serviceDomain[ServiceDomainGetListSchema.name] &&
                serviceDomain[ServiceDomainGetListSchema.name].toLowerCase().includes(searchString.toLowerCase())
            ));
            setServiceDomainFilteredList(filteredList);
        }
        else {
            setServiceDomainFilteredList(serviceDomainList);
        }
    }, [serviceDomainList, searchString]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    useEffect(() => {
        if (isOpen) {
            setSelectedObjects(defaultSelected || []);
        }
    }, [isOpen, defaultSelected]);

    const handleSetServiceDomain = useCallback(() => {
        onSuccess && onSuccess(selectedObjects);
    }, [selectedObjects, onSuccess]);

    return (
        <SMCModal
            title={t('SMC_DOMAIN_WIZ_CHOOSE_TITLE')}
            height={620}
            width={690}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            errorMessage={errorMessage}
            reloadData={loadData}
        >
            <SMCModalBody lineHeight="normal">
                <SMCLabel bold>{t('SMC_DOMAIN_WIZ_CHOOSE_SEARCH')}</SMCLabel>
                <SMCTextInput
                    allowClearAll
                    suffixIcon="search"
                    value={searchString}
                    onChange={e => setSearchString(e.target.value)}
                />
                <SMCTable
                    loading={isTableDataLoading}
                    content={serviceDomainFilteredList}
                    columns={serviceDomainTableColumns}
                    sortBasis={sortBasis}
                    sortDirection={sortDirection}
                    setSort={setSort}
                    width={630}
                    height={350}
                    selectedObjects={selectedObjects}
                    setSelectedObjects={setSelectedObjects}
                    rowIdSelector={obj => obj[ServiceDomainGetListSchema.name]}
                    isRowSelectable={obj => Array.isArray(disableSelectById) ? !disableSelectById.includes(obj[ServiceDomainGetListSchema.name]) : true}
                    hasCheckAll={true}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={handleSetServiceDomain}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_APPLY')}
                </SMCButton>
                <SMCButton onClick={onRequestClose}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default SetServiceDomainModal;